'use client';
/**
 * LE RÉGLAGE DES NOTIFICATIONS — Paramétrage → Alertes → Notifications.
 *
 * ── Pourquoi ce composant existe ───────────────────────────────────────────
 *
 * Il n'y avait AUCUN endroit atteignable pour régler qui est prévenu quand une
 * demande de rendez-vous ou un avis arrive du site. Le seul écran qui le
 * faisait (`manuel/.../PersonnalisationTab.tsx`) ne s'ouvrait que par l'URL
 * `?params=1` en profil administrateur — sans entrée de menu, donc introuvable.
 * Les adresses sont donc restées sur leurs défauts FICTIFS (`rdv@bio24.labo.tn`)
 * pendant des mois, et personne n'a jamais rien reçu.
 *
 * ── C'est ICI qu'on écrit, et nulle part ailleurs ──────────────────────────
 *
 * ⚠️ UN SEUL point d'écriture (arbitrage du 06/10/2026). L'écran
 * « Notifications & envois » LIT ces mêmes règles à côté du registre des
 * envois, et son bouton « Régler » ouvre cet onglet. Deux formulaires pour un
 * même réglage, c'est la garantie qu'un des deux soit décoratif.
 *
 * ── Le catalogue des événements est REÇU, jamais recopié ───────────────────
 *
 * ⚠️ Il vient de `GET /ged/notifications`, c'est-à-dire de
 * `backend/src/notifications.ts › EVENEMENTS`, qui est la seule liste. Le
 * recopier ici aurait créé deux listes pour une même vérité, avec le symptôme
 * le pire : un événement réglable à l'écran que le moteur n'écoute pas, ou
 * l'inverse. Même règle que `shared/iso15189`.
 *
 * Conséquence assumée : ce panneau a besoin d'un APPEL pour s'afficher, là où
 * le reste de l'onglet lit le brouillon déjà en mémoire. Il ne prétend donc
 * rien tant qu'il n'a pas lu — un panneau vide se lirait « aucun événement ne
 * notifie », ce qui serait faux et rassurant.
 */
import React from 'react';
import { I, inputStyle, Skeleton } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { useGed } from '@/lib/store';
import type { GedSettings } from '@/lib/types';

type RegleNotif = NonNullable<GedSettings['notif']['regles']>[number];

interface Condition { code: string; label: string; consequence: string }
interface Evenement {
  code: string; label: string; declencheur: string;
  tacheAppli: string | null; conditions: Condition[]; rolesDefaut: string[];
}
interface Porteur { nom: string; roles: string[]; joignable: boolean }
interface Famille {
  code: string; label: string; quoi: string;
  nature: 'opposable' | 'informative'; consequence: string;
}

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 4,
};

/** Contrôle de FORME, volontairement minimal — le même que le moteur
 *  (`notifications.ts › adresseValable`). On ne sait pas si une boîte existe ;
 *  on sait reconnaître ce qui n'a aucune chance d'arriver. */
const adresseValable = (a: string): boolean =>
  a.length >= 5 && a.length <= 254 && /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(a);

export function ReglesNotif({ value, onChange, familles, onChangeFamilles }: {
  value: RegleNotif[] | undefined;
  onChange: (regles: RegleNotif[]) => void;
  familles: Record<string, boolean> | undefined;
  onChangeFamilles: (familles: Record<string, boolean>) => void;
}) {
  const { go, closeSettings } = useGed();
  const [catalogue, setCatalogue] = React.useState<{
    evenements: Evenement[]; regles: RegleNotif[]; rolesNotifiables: string[]; porteurs: Porteur[];
    familles: Famille[]; famillesLabo: Record<string, boolean>;
  } | null>(null);
  const [erreur, setErreur] = React.useState('');
  /* Les adresses se saisissent en TEXTE LIBRE séparé par des virgules, et le
     texte en cours de frappe est gardé à part du réglage. Sans cela, écrire
     « a@b.tn, » perdait la virgule à chaque frappe (la liste normalisée se
     réaffichait sans elle) et il devenait impossible d'ajouter une 2e adresse. */
  const [brouillonAdresses, setBrouillonAdresses] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const r = await fetch('/api/ged/ged/notifications?limite=1', { credentials: 'include' });
        const j = await r.json().catch(() => null);
        if (!vivant) return;
        if (!r.ok) { setErreur(j?.error?.message || 'Le catalogue des notifications n’a pas pu être lu.'); return; }
        setCatalogue({
          evenements: j.evenements || [], regles: j.regles || [],
          rolesNotifiables: j.rolesNotifiables || [], porteurs: j.porteurs || [],
          familles: j.familles || [], famillesLabo: j.famillesLabo || {},
        });
      } catch { if (vivant) setErreur('Le service est injoignable : les règles ne peuvent pas être réglées pour l’instant.'); }
    })();
    return () => { vivant = false; };
  }, []);

  /* ⚠️ LA BASE DE TRAVAIL EST CELLE DU MOTEUR, pas `value`. `value` est le
     brouillon de l'onglet, qui vaut `undefined` tant que personne n'a rien
     touché — partir de là aurait affiché des cases décochées pour des règles
     ACTIVES par défaut, c'est-à-dire un écran qui dit l'inverse de la réalité.
     Le moteur, lui, rend toujours une règle normalisée par événement. */
  const regles = React.useMemo<RegleNotif[]>(() => {
    if (!catalogue) return [];
    const brouillon = new Map((value || []).map((r) => [r.evenement, r]));
    return catalogue.regles.map((r) => brouillon.get(r.evenement) || r);
  }, [catalogue, value]);

  const modifier = (code: string, champs: Partial<RegleNotif>) => {
    onChange(regles.map((r) => (r.evenement === code ? { ...r, ...champs } : r)));
  };

  if (erreur) {
    return (
      <div role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)', lineHeight: 1.6 }}>
        <strong>{erreur}</strong>
        <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          Les règles déjà enregistrées continuent de s’appliquer : rien n’a changé.
        </div>
      </div>
    );
  }
  if (!catalogue) return <Skeleton rows={3} height={92} />;

  return (
    <div>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.6, maxWidth: '88ch' }}>
        Qui est prévenu quand quelque chose arrive du site public. Ce qui est <strong>réellement
        parti</strong> se lit à l’écran <strong>Notifications &amp; envois</strong> (Pilotage &amp;
        revue) — un réglage dont on ne peut pas constater l’effet se règle à l’aveugle.
      </p>

      {/* ⚠️ LE CANAL « APPLI » N'EST PAS DÉCOCHABLE, et il faut le dire plutôt
          que de laisser chercher la case. Décocher les deux canaux aurait produit
          un événement dont plus rien n'avertit — un silence qu'aucun écran ne
          peut rattraper, puisqu'il n'y a par construction rien à afficher. */}
      <div style={{
        fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 16,
        padding: '10px 12px', background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)', maxWidth: '88ch',
      }}>
        <strong style={{ color: 'var(--text-strong)' }}>La cloche est toujours allumée.</strong>{' '}
        Seul le courriel se règle ici. C’est volontaire : c’est la trace minimale, celle qui
        subsiste même sans serveur d’envoi. Un événement dont les deux canaux seraient éteints
        n’avertirait plus personne, et aucun écran ne pourrait le signaler.
      </div>

      {catalogue.evenements.map((e) => {
        const r = regles.find((x) => x.evenement === e.code);
        if (!r) return null;
        const parRole = catalogue.porteurs.filter((p) => p.roles.some((x) => r.roles.includes(x)));
        const muets = parRole.filter((p) => !p.joignable);
        const texteAdresses = brouillonAdresses[e.code] ?? r.adresses.join(', ');
        const fautives = texteAdresses.split(',').map((a) => a.trim())
          .filter((a) => a && !adresseValable(a));
        const condition = e.conditions.find((c) => c.code === r.condition);
        /* Le cas qu'il faut crier : actif, courriel coché, et personne au bout.
           C'est l'état exact dans lequel le laboratoire a vécu des mois. */
        const sansPersonne = r.actif && r.courriel && !r.adresses.length
          && !parRole.some((p) => p.joignable);

        return (
          <fieldset key={e.code} style={{
            border: `1px solid ${sansPersonne ? 'var(--amber-600)' : 'var(--border-default)'}`,
            borderRadius: 'var(--radius-sm)', padding: '13px 15px', marginBottom: 12, minWidth: 0,
          }}>
            <legend style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900)', padding: '0 6px' }}>
              {e.label}
            </legend>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 11px', lineHeight: 1.55 }}>
              {e.declencheur}
              {e.tacheAppli
                ? <> La cloche signale « <strong>{e.tacheAppli}</strong> ».</>
                : <> <strong style={{ color: 'var(--amber-600)' }}>⚠️ Aucune tâche de cloche ne
                  porte ce fait : le courriel en est le seul signal.</strong></>}
            </p>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                <input type="checkbox" checked={r.actif}
                  onChange={(ev) => modifier(e.code, { actif: ev.target.checked })}
                  style={{ accentColor: 'var(--color-primary)' }} />
                <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>Règle active</span>
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: r.actif ? 'pointer' : 'not-allowed', opacity: r.actif ? 1 : 0.5 }}>
                <input type="checkbox" checked={r.courriel} disabled={!r.actif}
                  onChange={(ev) => modifier(e.code, { courriel: ev.target.checked })}
                  style={{ accentColor: 'var(--color-primary)' }} />
                <span style={{ color: 'var(--text-body)' }}>Prévenir par courriel</span>
              </label>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-muted)' }}>
                <I n="check" s={12} /> Prévenir dans la cloche <em>(toujours)</em>
              </span>
            </div>

            <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
              <div>
                <label style={lbl} htmlFor={`adr-${e.code}`}>Adresses à prévenir</label>
                <input id={`adr-${e.code}`} dir="ltr" value={texteAdresses}
                  placeholder="qualite@exemple.tn, accueil@exemple.tn"
                  onChange={(ev) => {
                    const brut = ev.target.value;
                    setBrouillonAdresses((b) => ({ ...b, [e.code]: brut }));
                    /* Seules les adresses BIEN FORMÉES entrent dans le réglage.
                       Les fautives restent visibles dans le champ et sont
                       nommées dessous : les effacer en silence aurait fait
                       croire à un enregistrement. */
                    modifier(e.code, {
                      adresses: [...new Set(brut.split(',').map((a) => a.trim().toLowerCase())
                        .filter(adresseValable))],
                    });
                  }}
                  style={{ ...inputStyle, width: '100%' }} />
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
                  Séparées par des virgules. Une boîte partagée survit à un départ.
                </div>
                {!!fautives.length && (
                  <div style={{ fontSize: 12, color: 'var(--red-600)', marginTop: 4, fontWeight: 600 }}>
                    Non enregistrée{fautives.length > 1 ? 's' : ''}, forme invalide : {fautives.join(', ')}
                  </div>
                )}
              </div>

              <div>
                <label style={lbl}>Rôles à prévenir</label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 5 }}>
                  {catalogue.rolesNotifiables.map((role) => (
                    <label key={role} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, cursor: 'pointer' }}>
                      <input type="checkbox" checked={r.roles.includes(role)}
                        onChange={(ev) => modifier(e.code, {
                          roles: ev.target.checked
                            ? [...new Set([...r.roles, role])]
                            : r.roles.filter((x) => x !== role),
                        })}
                        style={{ accentColor: 'var(--color-primary)' }} />
                      {role}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.5 }}>
                  {r.roles.length
                    ? (parRole.length
                      ? <>Aujourd’hui : <strong>{parRole.map((p) => p.nom).join(', ')}</strong>. Résolu à chaque envoi — une arrivée est prévenue sans rien changer ici.</>
                      : <strong style={{ color: 'var(--amber-600)' }}>⚠️ Personne ne porte ce rôle : aucun courriel ne partira par ce biais.</strong>)
                    : <>Un rôle suit l’organigramme, là où une adresse fixe ne bouge pas.</>}
                </div>
                {!!muets.length && (
                  <div style={{ fontSize: 12, color: 'var(--amber-600)', marginTop: 4, fontWeight: 600 }}>
                    ⚠️ Sans adresse de compte, donc non prévenu{muets.length > 1 ? 's' : ''} : {muets.map((p) => p.nom).join(', ')}
                  </div>
                )}
              </div>
            </div>

            {e.conditions.length > 1 && (
              <div style={{ marginTop: 12 }}>
                <label style={lbl} htmlFor={`cond-${e.code}`}>Quand envoyer</label>
                <select id={`cond-${e.code}`} value={r.condition}
                  onChange={(ev) => modifier(e.code, { condition: ev.target.value })}
                  style={{ ...inputStyle, maxWidth: 420 }}>
                  {e.conditions.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
                {/* ⚠️ La conséquence du filtre est AFFICHÉE : choisir
                    « seulement les réclamations » revient à cesser de recevoir
                    les avis positifs, et il faut le savoir avant de choisir,
                    pas en s'étonnant six mois plus tard. */}
                {condition && condition.code !== 'toutes' && (
                  <div style={{ fontSize: 12, color: 'var(--amber-600)', marginTop: 5, lineHeight: 1.55, maxWidth: '80ch' }}>
                    {condition.consequence}
                  </div>
                )}
              </div>
            )}

            {sansPersonne && (
              <div role="alert" style={{
                fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600, marginTop: 12,
                lineHeight: 1.55, maxWidth: '84ch',
              }}>
                ⚠️ Cette règle est active et son courriel est coché, mais <strong>aucun
                destinataire joignable</strong> n’est désigné : personne ne sera prévenu par
                courriel. C’est exactement l’état dans lequel le laboratoire a vécu jusqu’ici.
              </div>
            )}
          </fieldset>
        );
      })}

      {/* ══════════════════════════════════════════════════════════════════
          CE QUI NOTIFIE DANS LA CLOCHE (RQ, 06/10/2026)
          ══════════════════════════════════════════════════════════════════
          « Où est-ce que je peux désactiver les notifications liées aux
            maintenances ? » — nulle part, jusqu'ici. Les trois règles
          ci-dessus viennent TOUTES du site public ; les seize familles de la
          cloche, elles, n'étaient réglables à aucun endroit. */}
      <div style={{ marginTop: 26, paddingTop: 20, borderTop: '1px solid var(--border-subtle)' }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginBottom: 4 }}>
          Ce qui notifie dans la cloche
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 6px', lineHeight: 1.6, maxWidth: '88ch' }}>
          Ces notifications ne partent pas par courriel : elles s’affichent dans la cloche, en haut
          de l’écran. Décocher une famille la retire <strong>pour tout le personnel</strong>.
        </p>
        {/* ⚠️ LE LABORATOIRE BORNE, IL NE DONNE PAS — et il faut le dire, parce
            que c'est ce qui distingue ce réglage de la préférence personnelle.
            Sans cette phrase, on réglerait ici en croyant ne régler que pour
            soi. */}
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.6, maxWidth: '88ch' }}>
          Chacun peut en plus faire taire dans <em>sa</em> cloche ce qui ne le concerne pas, depuis
          la cloche elle-même. Ce réglage-ci <strong>borne</strong> : ce qu’il éteint, personne ne
          le rallume.
        </p>

        {(['opposable', 'informative'] as const).map((nature) => {
          const liste = catalogue.familles.filter((f) => f.nature === nature);
          if (!liste.length) return null;
          return (
            <div key={nature} style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em',
                color: 'var(--text-muted)', marginBottom: 8,
              }}>
                {nature === 'opposable' ? 'Échéances — elles restent dues' : 'Informations — elles ne demandent rien'}
              </div>
              {liste.map((f) => {
                const eteinte = (familles || catalogue.famillesLabo)[f.code] === false;
                return (
                  <div key={f.code} style={{
                    padding: '9px 11px', marginBottom: 6, borderRadius: 'var(--radius-sm)',
                    border: `1px solid ${eteinte ? 'var(--amber-600)' : 'var(--border-subtle)'}`,
                  }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!eteinte}
                        onChange={(ev) => {
                          const base = { ...(familles || catalogue.famillesLabo) };
                          /* ⚠️ On n'enregistre QUE les `false` : un `true` est
                             le défaut, et le stocker figerait ce défaut. */
                          if (ev.target.checked) delete base[f.code]; else base[f.code] = false;
                          onChangeFamilles(base);
                        }}
                        style={{ accentColor: 'var(--color-primary)', marginTop: 2 }} />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{f.label}</span>
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginTop: 2 }}>
                          {f.quoi}
                        </span>
                      </span>
                    </label>
                    {/* ⚠️ LA CONSÉQUENCE N'EST AFFICHÉE QUE SI L'ON DÉCOCHE.
                        C'est la condition posée le 06/10 pour accepter que les
                        échéances opposables soient décochables : le choix est à
                        la RQ, mais il doit être fait en sachant ce qu'on perd.
                        L'afficher en permanence l'aurait noyée dans du texte. */}
                    {eteinte && (
                      <div role="alert" style={{
                        fontSize: 12, color: 'var(--amber-600)', marginTop: 7, lineHeight: 1.55,
                        paddingInlineStart: 24, fontWeight: 600,
                      }}>
                        {f.consequence}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.6 }}>
        Pour vérifier qu’une règle aboutit réellement, envoyez un <strong>essai</strong> depuis
        l’écran Notifications &amp; envois : il emprunte le même chemin, les mêmes destinataires et
        le même gabarit qu’un vrai dépôt.
      </p>
      <div style={{ marginTop: 8 }}>
        {/* Navigation INTERNE, pas un nouvel onglet : l'écran a une adresse
            (`/notifications`), mais y aller par `window.open` aurait rechargé
            l'application entière et perdu le brouillon en cours de saisie. On
            referme le paramétrage et on navigue. */}
        <Bouton taille="sm" variante="discret" type="button"
          onClick={() => { closeSettings(); go('notifs'); }}>
          Ouvrir le registre des envois →
        </Bouton>
      </div>
    </div>
  );
}
